// An insurer's findings, on the lift page and the customer page (Session BBF). Between services an insurer's inspector examines a lift
// and lists items; the report reaches ELE, which has not seen them. This section records them against the lift (the insurer's wording
// copied exactly), books the separate, never-charged check an engineer makes beside the next service, and carries the office's two
// hand-offs afterwards: a Defect for an item the engineer confirmed is present, and a link to the repair estimate priced for one that
// needs parts or a return visit.
//
// WORDING RULES THIS SCREEN KEEPS, because it is where the office reads them. An insurer's finding is the INSURER'S: it is never
// called a fault ELE found, the wording is shown exactly as recorded, and the check is never charged and never sent to the customer.
// Nothing here emails anyone. The state of each item is the server's (derived live from the item and its job), never worked out here.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api, type RepairEstimateDto, type ScheduleEngineer } from "../../lib/api";
import { formatDate, formatDateTime } from "../../lib/format";
import { STATE_TONE, type CheckJobPlan, type InsuranceItemDto } from "../../lib/insuranceItems";
import { Button } from "../../components/Button";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { MODAL_ACTIONS, MODAL_PRIMARY_BTN, MODAL_SECONDARY_BTN, ModalShell } from "../../components/ModalShell";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const errText = (err: unknown, fallback: string): string => (err instanceof ApiError ? err.message || err.code : fallback);

// The reference a person types for a date: DD/MM/YYYY is how a report prints it, and the server wants YYYY-MM-DD.
function isoDay(v: string): string | null {
  const t = v.trim();
  if (!t) return null;
  const uk = /^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/.exec(t);
  if (uk) return `${uk[3]}-${uk[2]!.padStart(2, "0")}-${uk[1]!.padStart(2, "0")}`;
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : "invalid";
}

export function InsuranceItemsSection({
  assetId,
  customerId,
  liftLabel,
  className = "",
}: {
  /** On a lift page: this lift only, with the controls to record a report and book the check. */
  assetId?: string;
  /** On a customer page: every lift of this customer, read only apart from the per-item actions. */
  customerId?: string;
  liftLabel?: string | null;
  className?: string;
}) {
  const [items, setItems] = useState<InsuranceItemDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    try {
      const r = await api.insuranceItems.list({ assetId, customerId, includeArchived: showArchived, limit: 200 });
      if (!mounted.current) return;
      setItems(r.items);
      setLoadError(null);
    } catch (err) {
      if (!mounted.current) return;
      setLoadError(errText(err, "Could not load the insurer's findings."));
      setItems((cur) => cur ?? []);
    }
  }, [assetId, customerId, showArchived]);
  useEffect(() => {
    void reload();
  }, [reload]);

  const [editing, setEditing] = useState<InsuranceItemDto | "new" | null>(null);
  const [booking, setBooking] = useState(false);
  const [deleting, setDeleting] = useState<InsuranceItemDto | null>(null);
  const [defecting, setDefecting] = useState<InsuranceItemDto | null>(null);
  const [linking, setLinking] = useState<InsuranceItemDto | null>(null);

  const live = (items ?? []).filter((i) => !i.archived);
  const bookable = live.filter((i) => i.offers.bookCheck).length;

  async function archive(item: InsuranceItemDto, restore: boolean) {
    setActionError(null);
    try {
      if (restore) await api.insuranceItems.restore(item.id);
      else await api.insuranceItems.archive(item.id);
      setNotice(restore ? "The finding is back on the lift's record." : "The finding is archived. It stays on the lift's record and can be restored.");
      await reload();
    } catch (err) {
      setActionError(errText(err, "That did not work."));
    }
  }

  async function unlink(item: InsuranceItemDto) {
    setActionError(null);
    try {
      await api.insuranceItems.linkEstimate(item.id, null);
      setNotice("The estimate is no longer linked to that finding.");
      await reload();
    } catch (err) {
      setActionError(errText(err, "That did not work."));
    }
  }

  return (
    <section className={`rounded-ele border border-ele-purple-accent bg-ele-surface p-5 ${className}`} aria-labelledby="insurer-findings-heading">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h2 id="insurer-findings-heading" className="font-heading text-lg text-ele-blue">
          Insurer findings{live.length > 0 ? ` (${live.length})` : ""}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {assetId && bookable > 0 && (
            <Button size="sm" variant="secondary" onClick={() => setBooking(true)}>
              Book the check
            </Button>
          )}
          {assetId && (
            <Button size="sm" onClick={() => setEditing("new")}>
              Record a report
            </Button>
          )}
        </div>
      </div>
      <p className="mb-3 text-sm text-ele-body opacity-80">
        What an insurer's inspector reported about {liftLabel ? liftLabel : "a lift"}, in their own words. ELE has not seen these items itself until an engineer
        has checked them. They are not defects, not LOLER records and never charged.
      </p>
      {notice && (
        <p role="status" className="mb-3 rounded-ele border border-ele-green px-3 py-2 text-sm text-ele-strong">
          {notice}
        </p>
      )}
      {actionError && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-danger/10 px-3 py-2 text-sm text-ele-danger">
          {actionError}
        </p>
      )}
      {loadError && (
        <p role="alert" className="mb-3 text-sm text-ele-danger">
          {loadError}
        </p>
      )}
      {items === null ? (
        <p className="text-sm text-ele-body opacity-70">Loading.</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-ele-body opacity-70">No insurer findings are recorded{assetId ? " for this lift" : " for this customer"}.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => (
            <li key={it.id} className="rounded-ele border border-ele-purple-accent bg-ele-field p-3 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="min-w-0 font-menu text-[11px] uppercase tracking-wider text-ele-body opacity-80">
                  {!assetId && it.lift.eleNumber ? (
                    <>
                      <Link to={`/office/lifts/${encodeURIComponent(it.assetId)}`} className="text-ele-green hover:underline">
                        {it.lift.eleNumber}
                      </Link>
                      {it.site ? ` at ${it.site.name}. ` : ". "}
                    </>
                  ) : null}
                  {it.insurer}
                  {it.examinationDate ? `, examined ${formatDate(it.examinationDate)}` : ""}
                  {it.insurerCategory ? `, their category: ${it.insurerCategory}` : ""}
                  {it.reportReference ? `, report ${it.reportReference}` : ""}
                </p>
                <span className={`font-menu text-xs ${STATE_TONE[it.state]}`}>{it.stateLabel}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap break-words text-ele-strong">{it.wording}</p>
              {it.source.filename && <p className="mt-1 text-xs text-ele-body opacity-70">Report: {it.source.filename}</p>}
              {it.job && (
                <p className="mt-1 text-xs text-ele-body">
                  Check job {it.job.status.replace(/_/g, " ")}
                  {it.job.scheduledFor ? `, ${formatDate(it.job.scheduledFor)}` : ", no date yet"}
                  {it.job.engineerName ? `, ${it.job.engineerName}` : ""}.{" "}
                  <Link to={`/office/jobsheets/${encodeURIComponent(it.job.id)}`} className="text-ele-green hover:underline">
                    Open the visit
                  </Link>
                </p>
              )}
              {it.outcome && (
                <p className="mt-2 rounded-ele border border-ele-purple-accent px-2 py-1.5 text-ele-strong">
                  <span className="font-menu text-[11px] uppercase tracking-wider text-ele-blue">Engineer's answer: </span>
                  {it.outcomeLabel}
                  {it.outcomeNote ? `. "${it.outcomeNote}"` : ""}
                  {it.outcome === "verified_present" && it.proposedSeverity ? ` Category set: ${it.proposedSeverity === "immediate" ? "Immediate (Category A)" : "Advisory"}.` : ""}
                  {it.photoCount > 0 ? ` ${it.photoCount} photo${it.photoCount === 1 ? "" : "s"} ticked.` : ""}
                </p>
              )}
              {it.defect && (
                <p className="mt-1 text-xs text-ele-body">
                  Defect raised ({it.defect.severity === "immediate" ? "Immediate" : "Advisory"}, {it.defect.status}).{" "}
                  <Link to={`/office/defects/${encodeURIComponent(it.defect.id)}`} className="text-ele-green hover:underline">
                    Open defect
                  </Link>
                </p>
              )}
              {it.repairEstimate && <p className="mt-1 text-xs text-ele-body">Repair estimate linked: {it.repairEstimate.reference} ({it.repairEstimate.status}).</p>}
              <div className="mt-2 flex flex-wrap gap-2">
                {it.offers.raiseDefect && (
                  <Button size="sm" variant="secondary" onClick={() => setDefecting(it)}>
                    Raise a defect
                  </Button>
                )}
                {it.offers.raiseEstimate && (
                  <Button size="sm" variant="secondary" onClick={() => setLinking(it)}>
                    Link a repair estimate
                  </Button>
                )}
                {it.repairEstimate && !it.archived && (
                  <Button size="sm" variant="ghost" onClick={() => void unlink(it)}>
                    Unlink the estimate
                  </Button>
                )}
                {!it.archived && (
                  <Button size="sm" variant="ghost" onClick={() => setEditing(it)}>
                    Edit
                  </Button>
                )}
                {it.archived ? (
                  <Button size="sm" variant="ghost" onClick={() => void archive(it, true)}>
                    Restore
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => void archive(it, false)}>
                    Archive
                  </Button>
                )}
                <Button size="sm" variant="destructive" onClick={() => setDeleting(it)}>
                  Delete
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <label className="mt-3 flex min-h-11 items-center gap-2 text-xs text-ele-body">
        <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="h-4 w-4 accent-ele-green" />
        Show archived findings
      </label>

      {editing && assetId !== undefined && (
        <RecordModal
          assetId={editing === "new" ? assetId : editing.assetId}
          existing={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async (msg) => {
            setEditing(null);
            setNotice(msg);
            await reload();
          }}
        />
      )}
      {editing && assetId === undefined && editing !== "new" && (
        <RecordModal
          assetId={editing.assetId}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={async (msg) => {
            setEditing(null);
            setNotice(msg);
            await reload();
          }}
        />
      )}
      {booking && assetId && (
        <BookCheckModal
          assetId={assetId}
          onClose={() => setBooking(false)}
          onBooked={async (msg) => {
            setBooking(false);
            setNotice(msg);
            await reload();
          }}
        />
      )}
      {defecting && (
        <RaiseDefectModal
          item={defecting}
          onClose={() => setDefecting(null)}
          onRaised={async (msg) => {
            setDefecting(null);
            setNotice(msg);
            await reload();
          }}
        />
      )}
      {linking && (
        <LinkEstimateModal
          item={linking}
          onClose={() => setLinking(null)}
          onLinked={async (msg) => {
            setLinking(null);
            setNotice(msg);
            await reload();
          }}
        />
      )}
      <DeleteItem
        item={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={async () => {
          setDeleting(null);
          setNotice("The finding is deleted.");
          await reload();
        }}
      />
    </section>
  );
}

// ---- Delete: the platform's two-step confirm, with the attachment guard's reason shown plainly ---------------------------------

function DeleteItem({ item, onClose, onDeleted }: { item: InsuranceItemDto | null; onClose: () => void; onDeleted: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setError(null), [item?.id]);
  const blocked = item ? item.outcome != null || item.job != null || item.defect != null || item.repairEstimate != null : false;
  return (
    <ConfirmDeleteModal
      open={item != null}
      title="Delete this finding?"
      message={
        blocked
          ? "This finding has an engineer's answer, a check job, a defect or an estimate attached, so it cannot be deleted: it is part of the lift's record. Archive it instead and it stays on the record."
          : "This permanently deletes a finding that was recorded by mistake. It cannot be undone."
      }
      warning={blocked ? "Archive it instead." : null}
      confirmLabel="Delete"
      busy={busy}
      error={error}
      onCancel={() => !busy && onClose()}
      onConfirm={() => {
        if (!item) return;
        setBusy(true);
        setError(null);
        api.insuranceItems
          .remove(item.id)
          .then(() => onDeleted())
          .catch((err) => setError(errText(err, "Could not delete the finding.")))
          .finally(() => setBusy(false));
      }}
    />
  );
}

// ---- Record / edit -------------------------------------------------------------------------------------------------------------

interface DraftItem {
  wording: string;
  insurerCategory: string;
}

function RecordModal({
  assetId,
  existing,
  onClose,
  onSaved,
}: {
  assetId: string;
  existing: InsuranceItemDto | null;
  onClose: () => void;
  onSaved: (message: string) => Promise<void>;
}) {
  const [insurer, setInsurer] = useState(existing?.insurer ?? "");
  const [reportReference, setReportReference] = useState(existing?.reportReference ?? "");
  const [reportDate, setReportDate] = useState(existing?.reportDate ?? "");
  const [examinationDate, setExaminationDate] = useState(existing?.examinationDate ?? "");
  const [sourceFilename, setSourceFilename] = useState(existing?.source.filename ?? "");
  const [drafts, setDrafts] = useState<DraftItem[]>([{ wording: existing?.wording ?? "", insurerCategory: existing?.insurerCategory ?? "" }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const answered = existing?.outcome != null;

  async function save() {
    setError(null);
    const report = isoDay(reportDate);
    const exam = isoDay(examinationDate);
    if (report === "invalid" || exam === "invalid") {
      setError("Dates are DD/MM/YYYY, as the report prints them.");
      return;
    }
    if (!insurer.trim()) {
      setError("Name the insurer.");
      return;
    }
    const filled = drafts.filter((d) => d.wording.trim() !== "");
    if (filled.length === 0) {
      setError("Paste the insurer's wording for at least one finding.");
      return;
    }
    setBusy(true);
    try {
      if (existing) {
        await api.insuranceItems.update(existing.id, {
          insurer: insurer.trim(),
          reportReference: reportReference.trim() || null,
          reportDate: report,
          examinationDate: exam,
          insurerCategory: drafts[0]!.insurerCategory.trim() || null,
          sourceFilename: sourceFilename.trim() || null,
          ...(answered ? {} : { wording: filled[0]!.wording }),
        });
        await onSaved("The finding is updated.");
        return;
      }
      const res = await api.insuranceItems.record({
        assetId,
        insurer: insurer.trim(),
        reportReference: reportReference.trim() || null,
        reportDate: report,
        examinationDate: exam,
        sourceFilename: sourceFilename.trim() || null,
        items: filled.map((d) => ({ wording: d.wording, insurerCategory: d.insurerCategory.trim() || null })),
      });
      const made = res.created.length;
      const skipped = res.skipped.length;
      await onSaved(
        `${made} finding${made === 1 ? "" : "s"} recorded as the insurer reported ${made === 1 ? "it" : "them"}.${skipped > 0 ? ` ${skipped} already on record, so not added again.` : ""} No check is booked yet.`,
      );
    } catch (err) {
      setError(errText(err, "Could not save the findings."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel={existing ? "Edit insurer finding" : "Record an insurer's report"} onClose={() => !busy && onClose()} width="max-w-[560px]" panelClassName="p-6 max-h-[88vh] overflow-y-auto">
      <h2 className="font-heading text-xl text-ele-strong">{existing ? "Edit the finding" : "Record an insurer's report"}</h2>
      <p className="mb-3 text-sm text-ele-body">
        Copy each finding exactly as the report words it. Do not tidy, shorten or combine them. This records what the insurer reported, not a fault we found, and it books nothing.
      </p>
      <div className="space-y-3">
        <div>
          <label htmlFor="ins-insurer" className={labelCls}>
            Insurer
          </label>
          <input id="ins-insurer" className={inputCls} value={insurer} onChange={(e) => setInsurer(e.target.value)} placeholder="As the report names them" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="ins-exam" className={labelCls}>
              Examined on
            </label>
            <input id="ins-exam" className={inputCls} value={examinationDate} onChange={(e) => setExaminationDate(e.target.value)} placeholder="DD/MM/YYYY" />
          </div>
          <div>
            <label htmlFor="ins-report-date" className={labelCls}>
              Report dated
            </label>
            <input id="ins-report-date" className={inputCls} value={reportDate} onChange={(e) => setReportDate(e.target.value)} placeholder="DD/MM/YYYY" />
          </div>
          <div>
            <label htmlFor="ins-ref" className={labelCls}>
              Insurer's report number
            </label>
            <input id="ins-ref" className={inputCls} value={reportReference} onChange={(e) => setReportReference(e.target.value)} />
          </div>
          <div>
            <label htmlFor="ins-file" className={labelCls}>
              Report filename
            </label>
            <input id="ins-file" className={inputCls} value={sourceFilename} onChange={(e) => setSourceFilename(e.target.value)} placeholder="So it can be found again" />
          </div>
        </div>
        {drafts.map((d, i) => (
          <div key={i} className="rounded-ele border border-ele-purple-accent p-3">
            <label htmlFor={`ins-wording-${i}`} className={labelCls}>
              {existing ? "The insurer's wording" : `Finding ${i + 1}: the insurer's wording`}
            </label>
            <textarea
              id={`ins-wording-${i}`}
              rows={3}
              className={inputCls}
              value={d.wording}
              disabled={answered}
              onChange={(e) => setDrafts((cur) => cur.map((x, j) => (j === i ? { ...x, wording: e.target.value } : x)))}
            />
            {answered && <p className="mt-1 text-xs text-ele-body opacity-70">The wording cannot be changed once an engineer has answered it.</p>}
            <label htmlFor={`ins-cat-${i}`} className={`${labelCls} mt-2`}>
              The insurer's own category (as stated, optional)
            </label>
            <input
              id={`ins-cat-${i}`}
              className={inputCls}
              value={d.insurerCategory}
              onChange={(e) => setDrafts((cur) => cur.map((x, j) => (j === i ? { ...x, insurerCategory: e.target.value } : x)))}
              placeholder="e.g. A defect, Other defect, Recommendation"
            />
            {!existing && drafts.length > 1 && (
              <Button size="sm" variant="ghost" className="mt-2" onClick={() => setDrafts((cur) => cur.filter((_, j) => j !== i))}>
                Remove this finding
              </Button>
            )}
          </div>
        ))}
        {!existing && (
          <Button size="sm" variant="secondary" onClick={() => setDrafts((cur) => [...cur, { wording: "", insurerCategory: cur[cur.length - 1]?.insurerCategory ?? "" }])}>
            Add another finding
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-danger/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
      <div className={`mt-4 gap-3 ${MODAL_ACTIONS}`}>
        <button type="button" className={MODAL_SECONDARY_BTN} onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className={MODAL_PRIMARY_BTN} onClick={() => void save()} disabled={busy}>
          {busy ? "Saving..." : existing ? "Save changes" : "Record the findings"}
        </button>
      </div>
    </ModalShell>
  );
}

// ---- Book the check ------------------------------------------------------------------------------------------------------------

function whenText(iso: string | null): string {
  return iso ? formatDateTime(iso) : "no date yet";
}

function BookCheckModal({ assetId, onClose, onBooked }: { assetId: string; onClose: () => void; onBooked: (message: string) => Promise<void> }) {
  const [plan, setPlan] = useState<CheckJobPlan | null>(null);
  const [engineers, setEngineers] = useState<ScheduleEngineer[]>([]);
  const [day, setDay] = useState("");
  const [engineerId, setEngineerId] = useState("");
  const [minutes, setMinutes] = useState("60");
  const [standalone, setStandalone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const body = useMemo(() => {
    const d = isoDay(day);
    const m = Number.parseInt(minutes, 10);
    return {
      assetId,
      ...(d && d !== "invalid" ? { scheduledDay: d } : {}),
      ...(engineerId ? { engineerId } : {}),
      ...(Number.isInteger(m) && m >= 5 && m <= 480 ? { durationMins: m } : {}),
      ...(standalone ? { standalone: true } : {}),
    };
  }, [assetId, day, engineerId, minutes, standalone]);

  useEffect(() => {
    api.scheduling.engineers().then(setEngineers).catch(() => setEngineers([]));
  }, []);
  useEffect(() => {
    let cancelled = false;
    setError(null);
    if (isoDay(day) === "invalid") {
      setPlan(null);
      setError("Dates are DD/MM/YYYY.");
      return;
    }
    api.insuranceItems
      .checkJob({ ...body, preview: true })
      .then((r) => !cancelled && setPlan(r.plan ?? null))
      .catch((err) => {
        if (cancelled) return;
        setPlan(null);
        setError(errText(err, "Could not plan the check."));
      });
    return () => {
      cancelled = true;
    };
  }, [body, day]);

  async function book() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.insuranceItems.checkJob({ ...body, preview: false });
      await onBooked(r.joined ? "The findings joined the check already booked for this lift." : "The insurance check is booked. It is never charged and nothing is sent to the customer.");
    } catch (err) {
      setError(errText(err, "Could not book the check."));
    } finally {
      setBusy(false);
    }
  }

  const act = plan?.action;
  return (
    <ModalShell ariaLabel="Book the insurance check" onClose={() => !busy && onClose()} width="max-w-[560px]" panelClassName="p-6 max-h-[88vh] overflow-y-auto">
      <h2 className="font-heading text-xl text-ele-strong">Book the insurance check</h2>
      <p className="mb-3 text-sm text-ele-body">
        A separate job an engineer does beside the lift's next service, to check what the insurer reported. It is never charged and nothing is sent to the customer. Nothing is booked until you confirm.
      </p>
      {plan && (
        <div className="mb-3 rounded-ele border border-ele-purple-accent p-3 text-sm text-ele-strong">
          <p className="font-menu text-xs uppercase tracking-wide text-ele-blue">The plan</p>
          <p className="mt-1">
            {act?.kind === "create" && (
              <>
                A new check for {plan.items.length} finding{plan.items.length === 1 ? "" : "s"}: {whenText(act.scheduledFor)}, {act.engineerName ?? "no engineer yet"}, planned for {act.durationMins} minutes.
              </>
            )}
            {act?.kind === "join" && <>The {plan.items.length} finding{plan.items.length === 1 ? "" : "s"} join the check already booked for {whenText(act.scheduledFor)}.</>}
            {act?.kind === "wait" && <>This lift's check has already started, so these findings wait for the next one.</>}
          </p>
          {plan.notes.map((n) => (
            <p key={n} className="mt-1 text-xs text-ele-body">
              {n}
            </p>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="chk-day" className={labelCls}>
            A day of your choice (optional)
          </label>
          <input id="chk-day" className={inputCls} value={day} onChange={(e) => setDay(e.target.value)} placeholder="DD/MM/YYYY" />
        </div>
        <div>
          <label htmlFor="chk-eng" className={labelCls}>
            Engineer (optional)
          </label>
          <select id="chk-eng" className={inputCls} value={engineerId} onChange={(e) => setEngineerId(e.target.value)}>
            <option value="">Same as the service, or none</option>
            {engineers.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="chk-mins" className={labelCls}>
            Planned minutes on site
          </label>
          <input id="chk-mins" type="number" min={5} max={480} className={inputCls} value={minutes} onChange={(e) => setMinutes(e.target.value)} />
        </div>
        <label className="flex min-h-11 items-center gap-2 self-end text-sm text-ele-body">
          <input type="checkbox" checked={standalone} onChange={(e) => setStandalone(e.target.checked)} className="h-4 w-4 accent-ele-green" />
          Not tied to a service visit
        </label>
      </div>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-danger/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
      <div className={`mt-4 gap-3 ${MODAL_ACTIONS}`}>
        <button type="button" className={MODAL_SECONDARY_BTN} onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className={MODAL_PRIMARY_BTN} onClick={() => void book()} disabled={busy || !plan || act?.kind === "wait"}>
          {busy ? "Booking..." : "Book the check"}
        </button>
      </div>
    </ModalShell>
  );
}

// ---- Raise a defect from a confirmed-present finding ---------------------------------------------------------------------------

function RaiseDefectModal({ item, onClose, onRaised }: { item: InsuranceItemDto; onClose: () => void; onRaised: (message: string) => Promise<void> }) {
  const [severity, setSeverity] = useState<"advisory" | "immediate">(item.proposedSeverity ?? "advisory");
  const [description, setDescription] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    api.insuranceItems
      .raiseDefect(item.id, { severity, preview: true })
      .then((r) => !cancelled && setDescription(r.description ?? null))
      .catch((err) => !cancelled && setError(errText(err, "Could not prepare the defect.")));
    return () => {
      cancelled = true;
    };
  }, [item.id, severity]);
  async function raise() {
    setBusy(true);
    setError(null);
    try {
      await api.insuranceItems.raiseDefect(item.id, { severity, preview: false });
      await onRaised("The defect is raised on the lift.");
    } catch (err) {
      setError(errText(err, "Could not raise the defect."));
    } finally {
      setBusy(false);
    }
  }
  return (
    <ModalShell ariaLabel="Raise a defect from this finding" onClose={() => !busy && onClose()} panelClassName="p-6 max-h-[88vh] overflow-y-auto">
      <h2 className="font-heading text-xl text-ele-strong">Raise a defect?</h2>
      <p className="mb-3 text-sm text-ele-body">
        The engineer confirmed this is present. The defect says the insurer reported it and our engineer confirmed it. It does not say we found it. Nothing is sent to the customer.
      </p>
      <p className="mb-3 whitespace-pre-wrap rounded-ele border border-ele-purple-accent p-3 text-sm text-ele-strong">{description ?? "Preparing the wording."}</p>
      <label htmlFor="def-sev" className={labelCls}>
        Category (the engineer set {item.proposedSeverity === "immediate" ? "Immediate" : "Advisory"})
      </label>
      <select id="def-sev" className={inputCls} value={severity} onChange={(e) => setSeverity(e.target.value as "advisory" | "immediate")}>
        <option value="advisory">Advisory</option>
        <option value="immediate">Immediate (Category A)</option>
      </select>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-danger/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
      <div className={`mt-4 gap-3 ${MODAL_ACTIONS}`}>
        <button type="button" className={MODAL_SECONDARY_BTN} onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className={MODAL_PRIMARY_BTN} onClick={() => void raise()} disabled={busy || !description}>
          {busy ? "Raising..." : "Raise the defect"}
        </button>
      </div>
    </ModalShell>
  );
}

// ---- Link the estimate the office priced ---------------------------------------------------------------------------------------

function LinkEstimateModal({ item, onClose, onLinked }: { item: InsuranceItemDto; onClose: () => void; onLinked: (message: string) => Promise<void> }) {
  const [options, setOptions] = useState<RepairEstimateDto[] | null>(null);
  const [chosen, setChosen] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!item.customer) {
      setOptions([]);
      return;
    }
    api.repairEstimates
      .list({ customerId: item.customer.id })
      .then((all) => setOptions(all.filter((e) => e.assetId === item.assetId)))
      .catch(() => setOptions([]));
  }, [item.assetId, item.customer]);
  async function link() {
    if (!chosen) return;
    setBusy(true);
    setError(null);
    try {
      await api.insuranceItems.linkEstimate(item.id, chosen);
      await onLinked("The repair estimate is linked to the finding.");
    } catch (err) {
      setError(errText(err, "Could not link the estimate."));
    } finally {
      setBusy(false);
    }
  }
  return (
    <ModalShell ariaLabel="Link a repair estimate" onClose={() => !busy && onClose()} panelClassName="p-6">
      <h2 className="font-heading text-xl text-ele-strong">Link a repair estimate</h2>
      <p className="mb-3 text-sm text-ele-body">
        The engineer found this needs parts or a return visit. Price a repair estimate for the lift in the usual way, then link it here. Raising the repair stays your decision.
      </p>
      {options === null ? (
        <p className="text-sm text-ele-body opacity-70">Loading.</p>
      ) : options.length === 0 ? (
        <p className="text-sm text-ele-body">
          There is no repair estimate for this lift yet. Create one from the defects or call-outs page, then come back and link it.
        </p>
      ) : (
        <>
          <label htmlFor="est-pick" className={labelCls}>
            Repair estimate for this lift
          </label>
          <select id="est-pick" className={inputCls} value={chosen} onChange={(e) => setChosen(e.target.value)}>
            <option value="">Choose one</option>
            {options.map((e) => (
              <option key={e.id} value={e.id}>
                {e.reference}, {e.description.slice(0, 60)}
              </option>
            ))}
          </select>
        </>
      )}
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-danger/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
      <div className={`mt-4 gap-3 ${MODAL_ACTIONS}`}>
        <button type="button" className={MODAL_SECONDARY_BTN} onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className={MODAL_PRIMARY_BTN} onClick={() => void link()} disabled={busy || !chosen}>
          {busy ? "Linking..." : "Link the estimate"}
        </button>
      </div>
    </ModalShell>
  );
}
